import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { adminGet, adminPost, adminPut, adminDel, authHeaders } from "@/lib/adminApi";
import { Plus, ArrowUp, ArrowDown, Trash2, Power, Pencil, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { id: "ebook", label: "E-books", hint: "PDF vendus 9,90 € TTC — téléchargeables après paiement" },
  { id: "program", label: "Programmes", hint: "Parcours complets (audios, vidéos, images, mini-guides PDF dans l'ordre) — 29,90 € TTC" },
  { id: "book", label: "Livres", hint: "Albums physiques — le bouton ouvre simplement votre page Amazon" },
];

const EMPTY = { title: "", description: "", price: 9.9, cover_path: null, pdf_path: null, amazon_url: "", items: [], active: true };

// 19 — Boutique : Julie crée e-books, programmes et livres toute seule, et active
// le bouton menu du bandeau quand tout est prêt (interrupteur ci-dessous).
export default function Boutique() {
  const [section, setSection] = useState("ebook");
  const [items, setItems] = useState([]);
  const [media, setMedia] = useState([]);
  const [form, setForm] = useState(null);
  const [shopEnabled, setShopEnabled] = useState(false);
  const [settings, setSettings] = useState(null);
  const [uploading, setUploading] = useState("");
  const coverInput = useRef(null);
  const pdfInput = useRef(null);

  const load = () => adminGet(`/shop/${section}`).then((d) => setItems(d.items || [])).catch(() => setItems([]));
  const loadMedia = () => adminGet("/media").then((d) => setMedia(d.media || [])).catch(() => {});
  useEffect(() => {
    loadMedia();
    adminGet("/settings")
      .then((d) => {
        setSettings(d.settings || {});
        setShopEnabled(!!d.settings?.shop_enabled);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    setForm(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  // L'interrupteur : fait apparaître / disparaître le bouton menu du bandeau haut
  const toggleShop = async () => {
    const next = !shopEnabled;
    try {
      await adminPut("/settings", { ...(settings || {}), shop_enabled: next });
      setShopEnabled(next);
      toast(next ? "Boutique activée — le menu apparaît dans le bandeau des utilisatrices." : "Boutique masquée — le menu disparaît du bandeau.");
    } catch {
      toast("Une erreur est survenue.");
    }
  };

  const upload = async (e, field, isProtected = false) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(field);
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: m } = await axios.post(`${API}/admin/media${isProtected ? "?protected=1" : ""}`, data, { headers: authHeaders() });
      await loadMedia();
      setForm((f) => ({ ...f, [field]: m.storage_path }));
      toast(isProtected ? "PDF enregistré — il n'est téléchargeable qu'après achat." : "Image enregistrée.");
    } catch (err) {
      toast(err.response?.data?.detail || "Envoi impossible.");
    } finally {
      setUploading("");
    }
  };

  const save = async () => {
    if (!form.title?.trim()) {
      toast("Donnez un titre.");
      return;
    }
    try {
      const payload = { ...EMPTY, ...form, title: form.title.trim(), price: Number(form.price) || 0 };
      if (form.id) await adminPut(`/shop/${section}/${form.id}`, payload);
      else await adminPost(`/shop/${section}`, payload);
      toast("Enregistré dans la boutique.");
      setForm(null);
      load();
    } catch {
      toast("Une erreur est survenue.");
    }
  };

  const toggleActive = async (it) => {
    await adminPut(`/shop/${section}/${it.id}`, { ...it, active: !it.active }).catch(() => {});
    load();
  };

  const remove = async (it) => {
    await adminDel(`/shop/${section}/${it.id}`).catch(() => {});
    toast("Retiré.");
    load();
  };

  // Éditeur de contenus d'un programme : ordre précis, montée/descente
  const setItem = (i, patch) => setForm((f) => ({ ...f, items: f.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) }));
  const moveItem = (i, dir) =>
    setForm((f) => {
      const arr = [...f.items];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...f, items: arr };
    });

  // Mini-guide PDF d'un programme : envoyé en « protégé » — ouvrable uniquement
  // après achat, jamais par la médiathèque publique
  const uploadItemPdf = async (e, i) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(`item-${i}`);
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: m } = await axios.post(`${API}/admin/media?protected=1`, data, { headers: authHeaders() });
      await loadMedia();
      setItem(i, { storage_path: m.storage_path });
      toast("Guide PDF enregistré — lisible uniquement après achat du programme.");
    } catch (err) {
      toast(err.response?.data?.detail || "Envoi impossible.");
    } finally {
      setUploading("");
    }
  };

  const matchKind = (m, kind) => (kind === "pdf" ? m.content_type === "application/pdf" : m.content_type?.startsWith(kind));

  const images = media.filter((m) => m.content_type?.startsWith("image"));
  const pdfs = media.filter((m) => m.content_type === "application/pdf");
  const fileName = (path) => media.find((m) => m.storage_path === path)?.original_filename || path || "";
  const sec = SECTIONS.find((s) => s.id === section);

  return (
    <div data-testid="admin-boutique-module">
      <h2 className="rav-admin-h2">Boutique</h2>

      <div className="rav-admin-form rav-shop-toggle" data-testid="shop-toggle-row">
        <span>
          <strong>Boutique visible dans l'app</strong>
          <br />
          <span className="rav-admin-row-sub">Le bouton menu (e-books, programmes, livres) apparaît dans le bandeau haut des utilisatrices. Activez-le quand tout sera en place.</span>
        </span>
        <button type="button" className={`rav-secondary-button rav-session-btn`} data-testid="shop-toggle" onClick={toggleShop}>
          <Eye size={14} strokeWidth={1.7} /> {shopEnabled ? "Visible — masquer" : "Masquée — activer"}
        </button>
      </div>

      <div className="rav-chips rav-admin-chips-left">
        {SECTIONS.map((s) => (
          <button key={s.id} type="button" className={`rav-chip${section === s.id ? " is-selected" : ""}`} data-testid={`shop-section-${s.id}`} onClick={() => setSection(s.id)}>
            {s.label}
          </button>
        ))}
      </div>
      <p className="rav-page-sub rav-admin-note">{sec.hint}</p>

      <button type="button" className="rav-primary-button rav-admin-create" data-testid="shop-create" onClick={() => setForm({ ...EMPTY, price: section === "program" ? 29.9 : section === "book" ? 14.9 : 9.9 })}>
        <Plus size={14} strokeWidth={1.8} /> Ajouter
      </button>

      {form && (
        <div className="rav-admin-form" data-testid="shop-form">
          <input className="rav-input" data-testid="shop-form-title" placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <textarea className="rav-textarea" data-testid="shop-form-desc" placeholder="Courte description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="rav-admin-form-row">
            <input className="rav-input" data-testid="shop-form-price" type="number" step="0.01" min="0" placeholder="Prix TTC" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            <select className="rav-input" data-testid="shop-form-cover" value={form.cover_path || ""} onChange={(e) => setForm({ ...form, cover_path: e.target.value || null })}>
              <option value="">Aucune couverture</option>
              {images.map((m) => (
                <option key={m.id} value={m.storage_path}>{m.original_filename}</option>
              ))}
            </select>
            <input ref={coverInput} type="file" accept="image/*" hidden data-testid="shop-form-cover-input" onChange={(e) => upload(e, "cover_path")} />
            <button type="button" className="rav-text-link" data-testid="shop-form-cover-upload" disabled={!!uploading} onClick={() => coverInput.current?.click()}>
              {uploading === "cover_path" ? "Envoi…" : "Envoyer une couverture"}
            </button>
          </div>

          {section === "ebook" && (
            <div className="rav-admin-form-row">
              <select className="rav-input" data-testid="shop-form-pdf" value={form.pdf_path || ""} onChange={(e) => setForm({ ...form, pdf_path: e.target.value || null })}>
                <option value="">Aucun PDF</option>
                {pdfs.map((m) => (
                  <option key={m.id} value={m.storage_path}>{m.original_filename}</option>
                ))}
              </select>
              <input ref={pdfInput} type="file" accept="application/pdf,.pdf" hidden data-testid="shop-form-pdf-input" onChange={(e) => upload(e, "pdf_path", true)} />
              <button type="button" className="rav-text-link" data-testid="shop-form-pdf-upload" disabled={!!uploading} onClick={() => pdfInput.current?.click()}>
                {uploading === "pdf_path" ? "Envoi…" : "Envoyer le PDF"}
              </button>
              {form.pdf_path && <span className="rav-admin-row-sub">{fileName(form.pdf_path)}</span>}
            </div>
          )}

          {section === "book" && (
            <input className="rav-input" data-testid="shop-form-amazon" type="url" inputMode="url" autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder="Lien Amazon (https://…)" value={form.amazon_url || ""} onChange={(e) => setForm({ ...form, amazon_url: e.target.value })} />
          )}

          {section === "program" && (
            <div data-testid="shop-form-items">
              <p className="rav-page-sub rav-admin-note">Contenus du programme, dans l'ordre exact de déroulement :</p>
              {(form.items || []).map((it, i) => (
                <div key={i} className="rav-admin-form-row" data-testid={`shop-item-row-${i}`}>
                  <input className="rav-input" data-testid={`shop-item-title-${i}`} placeholder={`Titre du contenu ${i + 1}`} value={it.title} onChange={(e) => setItem(i, { title: e.target.value })} />
                  <select className="rav-input" data-testid={`shop-item-kind-${i}`} value={it.kind} onChange={(e) => setItem(i, { kind: e.target.value, storage_path: null })}>
                    <option value="audio">Audio</option>
                    <option value="video">Vidéo</option>
                    <option value="image">Image</option>
                    <option value="pdf">Guide PDF</option>
                  </select>
                  <select className="rav-input" data-testid={`shop-item-media-${i}`} value={it.storage_path || ""} onChange={(e) => setItem(i, { storage_path: e.target.value || null })}>
                    <option value="">Choisir le fichier…</option>
                    {media.filter((m) => matchKind(m, it.kind)).map((m) => (
                      <option key={m.id} value={m.storage_path}>{m.original_filename}</option>
                    ))}
                  </select>
                  {it.kind === "pdf" && (
                    <>
                      <input type="file" accept="application/pdf,.pdf" hidden data-testid={`shop-item-pdf-input-${i}`} onChange={(e) => uploadItemPdf(e, i)} id={`shop-item-pdf-${i}`} />
                      <button type="button" className="rav-text-link" data-testid={`shop-item-pdf-upload-${i}`} disabled={!!uploading} onClick={() => document.getElementById(`shop-item-pdf-${i}`)?.click()}>
                        {uploading === `item-${i}` ? "Envoi…" : "Envoyer le PDF"}
                      </button>
                    </>
                  )}
                  {it.storage_path && (
                    <span className="rav-admin-row-sub" data-testid={`shop-item-file-${i}`}>{fileName(it.storage_path)}</span>
                  )}
                  <button type="button" className="rav-text-link" data-testid={`shop-item-up-${i}`} onClick={() => moveItem(i, -1)} aria-label="Monter"><ArrowUp size={14} /></button>
                  <button type="button" className="rav-text-link" data-testid={`shop-item-down-${i}`} onClick={() => moveItem(i, 1)} aria-label="Descendre"><ArrowDown size={14} /></button>
                  <button type="button" className="rav-text-link rav-note-delete" data-testid={`shop-item-del-${i}`} onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, j) => j !== i) }))} aria-label="Retirer"><Trash2 size={14} /></button>
                </div>
              ))}
              <button type="button" className="rav-text-link" data-testid="shop-item-add" onClick={() => setForm((f) => ({ ...f, items: [...(f.items || []), { title: "", kind: "audio", storage_path: null }] }))}>
                <Plus size={13} strokeWidth={1.8} /> Ajouter un contenu
              </button>
            </div>
          )}

          <div className="rav-session-actions">
            <button type="button" className="rav-secondary-button rav-session-btn" data-testid="shop-form-save" onClick={save}>
              Enregistrer
            </button>
            <button type="button" className="rav-text-link" data-testid="shop-form-cancel" onClick={() => setForm(null)}>
              Annuler
            </button>
          </div>
        </div>
      )}

      <div className="rav-admin-list">
        {items.map((it, i) => (
          <div key={it.id} className="rav-admin-row" data-testid={`shop-row-${i}`}>
            <span className="rav-admin-row-label">
              {it.title}
              {!it.active && <span className="rav-admin-row-sub"> · masqué</span>}
            </span>
            <span className="rav-admin-row-sub">
              {Number(it.price || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €
              {section === "ebook" && (it.pdf_path ? ` · PDF : ${fileName(it.pdf_path)}` : " · sans PDF")}
              {section === "program" && ` · ${(it.items || []).length} contenu(s)`}
              {section === "book" && (it.amazon_url ? " · lien Amazon en place" : " · sans lien Amazon")}
            </span>
            <span className="rav-admin-row-actions">
              <button type="button" className="rav-text-link" data-testid={`shop-edit-${i}`} onClick={() => setForm({ ...EMPTY, ...it })}>
                <Pencil size={13} strokeWidth={1.6} /> Modifier
              </button>
              <button type="button" className="rav-text-link" data-testid={`shop-toggle-${i}`} onClick={() => toggleActive(it)}>
                <Power size={13} strokeWidth={1.6} /> {it.active ? "Masquer" : "Afficher"}
              </button>
              <button type="button" className="rav-text-link rav-note-delete" data-testid={`shop-delete-${i}`} onClick={() => remove(it)}>
                <Trash2 size={13} strokeWidth={1.6} /> Retirer
              </button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
